/**
 * The single owner of "is this money delta good news?" (#1778).
 *
 * Four independent branches of this question had drifted across ≥30 call
 * sites: a naive sign check (asset balances), an explicit `isDebtAccount`
 * inversion (chart tooltips), an income/expense split (category reports),
 * and a generalized `invertDelta` prop (report hero metrics) — each free to
 * pick its own zero-delta behavior and its own tone vocabulary.
 *
 * The `isDebtAccount` inversion was itself the bug: per
 * `docs/domain-invariants.md` §3.1, a debt balance is already stored
 * negative, so a rising signed balance is favorable for debt exactly the
 * same way it is for an asset — inverting it a second time flips it back to
 * wrong. `debt` and `asset` (and `income`, `transfer`) share one rule here;
 * only `expense` inverts, because spending less is the good outcome.
 */

export type FavorabilityScope = 'asset' | 'debt' | 'income' | 'expense' | 'transfer';

export type Favorability = 'favorable' | 'unfavorable' | 'neutral';

export interface FavorabilityOptions {
	/**
	 * Deadband around zero: `|delta| <= epsilon` is treated as no change.
	 * Defaults to 0 (exact zero only). Callers with a documented noise floor
	 * (e.g. a weekly spend comparison) can widen it — the deadband itself is
	 * still resolved through this one function, not re-implemented locally.
	 */
	epsilon?: number;
	/**
	 * Treat exact zero (within `epsilon`) as `'favorable'` instead of
	 * `'neutral'`. Only for callers classifying a static balance/snapshot
	 * rather than a period-over-period delta — a paid-off debt account
	 * (`balance === 0`) or a zero net-worth floor is a genuine milestone, not
	 * "nothing changed". Leave this `false` (the default) for anything that
	 * is actually a delta: the "zero delta renders one tone everywhere"
	 * contract only holds when every true delta site agrees on `'neutral'`.
	 */
	zeroIsFavorable?: boolean;
}

/**
 * Classify a signed money delta into a verdict for the given financial scope.
 *
 * `expense` is the one scope where a larger value is the worse outcome
 * (spending more); every other scope treats an increase as favorable. Zero
 * (within `epsilon`) is `'neutral'` by default — never silently folded into
 * favorable or unfavorable — because a caller choosing `>=0` for "on track"
 * is the exact bug class BillsPaydaysSection's `delta !== 0` guard exists to
 * prevent (see its own comment: overspending must never render green). Pass
 * `zeroIsFavorable` to opt a static-balance site (not a delta) out of that.
 */
export function favorability(
	delta: number,
	scope: FavorabilityScope,
	opts: FavorabilityOptions = {}
): Favorability {
	const epsilon = opts.epsilon ?? 0;
	if (Math.abs(delta) <= epsilon) return opts.zeroIsFavorable ? 'favorable' : 'neutral';

	const increaseIsFavorable = scope !== 'expense';
	const isIncrease = delta > 0;
	return isIncrease === increaseIsFavorable ? 'favorable' : 'unfavorable';
}

export type FavorabilityVocab =
	| 'success-destructive'
	| 'success-warning'
	| 'positive-muted'
	| 'success-warning-foreground';

/**
 * Map a verdict onto one of the tone vocabularies MASTER.md's "Status &
 * Badges" section documents in the wild. `success-warning-foreground` is the
 * one vocab that maps `neutral` to `text-foreground` instead of
 * `text-muted-foreground` (#1921, OccurrenceTimeline's "on-target" rows read
 * as plain body text, not dimmed) — every other vocab keeps `neutral` on the
 * calm/no-signal muted class, never a color, per PRD §7.
 */
export function favorabilityToneClass(verdict: Favorability, vocab: FavorabilityVocab): string {
	switch (vocab) {
		case 'success-destructive':
			return verdict === 'favorable'
				? 'text-success'
				: verdict === 'unfavorable'
					? 'text-destructive'
					: 'text-muted-foreground';
		case 'success-warning':
			return verdict === 'favorable'
				? 'text-success'
				: verdict === 'unfavorable'
					? 'text-warning'
					: 'text-muted-foreground';
		case 'positive-muted':
			return verdict === 'favorable' ? 'text-success' : 'text-muted-foreground';
		case 'success-warning-foreground':
			return verdict === 'favorable'
				? 'text-success'
				: verdict === 'unfavorable'
					? 'text-warning'
					: 'text-foreground';
	}
}

/**
 * Map a verdict onto the colorblind-safe chart tokens (`--chart-favorable` /
 * `--chart-unfavorable`, resolved via `chart-config.ts`). `neutral` returns
 * `null` — the existing chart convention is to omit the ternary and let the
 * mark inherit its default (muted) color, not to introduce a third chart
 * token.
 */
export function favorabilityChartVar(
	verdict: Favorability
): '--chart-favorable' | '--chart-unfavorable' | null {
	if (verdict === 'favorable') return '--chart-favorable';
	if (verdict === 'unfavorable') return '--chart-unfavorable';
	return null;
}
